import {
  Segment,
  PacingResult,
  RaceSettings,
  SegmentNutritionOutput,
  NutritionProduct,
  SegmentNutritionPlan,
  NutritionState,
} from '../types'

/**
 * Tolerance thresholds:
 * - Carbs: 90-110% of target
 * - Calories: 90-110% of target
 * - Sodium: 85-115% of target
 * - Fluids: guidance only, never affects state
 */
const CARB_TOLERANCE = { low: 0.9, high: 1.1 }
const CAL_TOLERANCE = { low: 0.9, high: 1.1 }
const SODIUM_TOLERANCE = { low: 0.85, high: 1.15 }

function isInRange(
  planned: number,
  target: number,
  tolerance: { low: number; high: number },
): boolean {
  if (target <= 0) return true
  const ratio = planned / target
  return ratio >= tolerance.low && ratio <= tolerance.high
}

function isBelowRange(
  planned: number,
  target: number,
  tolerance: { low: number },
): boolean {
  if (target <= 0) return false
  return planned / target < tolerance.low
}

function isAboveRange(
  planned: number,
  target: number,
  tolerance: { high: number },
): boolean {
  if (target <= 0) return false
  return planned / target > tolerance.high
}

function buildProductSummary(
  items: { productId: string; quantity: number }[],
  products: NutritionProduct[],
): string {
  if (!items || items.length === 0) return ''
  const parts = items
    .filter((i) => i.quantity > 0)
    .map((item) => {
      const product = products.find((p) => p.id === item.productId)
      if (!product) return null
      const qty = item.quantity === 1 ? '' : `${item.quantity}× `
      return `${qty}${product.name}`
    })
    .filter(Boolean)
  if (parts.length === 0) return ''
  if (parts.length <= 2) return parts.join(', ')
  return `${parts[0]}, ${parts[1]} +${parts.length - 2} more`
}

export function computeSegmentNutrition(
  segment: Segment,
  pacing: PacingResult,
  plan: SegmentNutritionPlan,
  settings: RaceSettings,
  products: NutritionProduct[],
): SegmentNutritionOutput {
  const params = settings.athleteProfile.params
  const climate = pacing.climateImpact
  const durationHours = pacing.duration / 60

  // 1. Calculate Target Rates (per hour)
  let targetChoGph = params.baselineChoPerHourG
  let targetCalKcalph = params.baselineCalPerHourKcal
  let targetFluidMlph = params.baselineFluidMlPerHour
  let targetSodiumMgph = 500 * params.sodiumMultiplier

  // Apply Climate Impact
  targetChoGph *= climate.carbFactor
  targetCalKcalph *= climate.carbFactor
  targetFluidMlph *= climate.hydrationFactor
  targetSodiumMgph *= climate.sodiumFactor

  // Apply Execution Strategy
  if (settings.executionScenario === 'standard') {
    targetChoGph *= 0.95
    targetCalKcalph *= 0.95
  } else if (settings.executionScenario === 'safe') {
    targetChoGph *= 0.9
    targetCalKcalph *= 0.9
    targetFluidMlph *= 1.1
    targetSodiumMgph *= 1.1
  }

  // Apply Absorption Decay (Late Race)
  if (pacing.accumulatedTime > params.fatigueOnsetMinutes) {
    const decayFactor = 1.0 - params.absorptionDecay
    targetChoGph *= decayFactor
    targetCalKcalph *= decayFactor
  }

  // Calculate Totals for Segment
  const targetChoG = targetChoGph * durationHours
  const targetCalKcal = targetCalKcalph * durationHours
  const targetFluidMl = targetFluidMlph * durationHours
  const targetSodiumMg = targetSodiumMgph * durationHours

  // 2. Calculate Planned Intake from products
  let plannedChoG = 0
  let plannedCalKcal = 0
  let plannedSodiumMg = 0
  const activeItems = (plan.intakeItems || []).filter((i) => i.quantity > 0)

  activeItems.forEach((item) => {
    const product = products.find((p) => p.id === item.productId)
    if (product) {
      plannedChoG += product.carbsPerUnitG * item.quantity
      plannedCalKcal += product.caloriesPerUnitKcal * item.quantity
      plannedSodiumMg += (product.sodiumPerUnitMg || 0) * item.quantity
    }
  })

  const hasProducts = activeItems.length > 0

  // 3. Determine dimension adequacy (only meaningful when products exist)
  const carbsInRange = isInRange(plannedChoG, targetChoG, CARB_TOLERANCE)
  const caloriesInRange = isInRange(
    plannedCalKcal,
    targetCalKcal,
    CAL_TOLERANCE,
  )
  const sodiumInRange = isInRange(
    plannedSodiumMg,
    targetSodiumMg,
    SODIUM_TOLERANCE,
  )

  // Check if values are specifically below target (needs adjustment)
  const carbsBelowTarget = isBelowRange(plannedChoG, targetChoG, CARB_TOLERANCE)
  const caloriesBelowTarget = isBelowRange(
    plannedCalKcal,
    targetCalKcal,
    CAL_TOLERANCE,
  )

  // Check if values are above target (informational only, still considered "planned")
  const carbsAboveTarget = isAboveRange(plannedChoG, targetChoG, CARB_TOLERANCE)
  const caloriesAboveTarget = isAboveRange(
    plannedCalKcal,
    targetCalKcal,
    CAL_TOLERANCE,
  )
  const sodiumAboveTarget = isAboveRange(
    plannedSodiumMg,
    targetSodiumMg,
    SODIUM_TOLERANCE,
  )

  // 4. Determine segment state
  // Above target = still on_target (green) with info note
  // Below target = needs_adjustment (orange)
  // Sodium is tracked but does NOT affect the nutrition score
  let state: NutritionState = 'not_planned'
  if (hasProducts) {
    const carbsOk = carbsInRange || carbsAboveTarget
    const caloriesOk = caloriesInRange || caloriesAboveTarget
    if (carbsOk && caloriesOk) {
      state = 'on_target'
    } else {
      state = 'needs_adjustment'
    }
  }

  // 5. Build human-readable product summary
  const productSummary = buildProductSummary(activeItems, products)

  // 6. Warnings (only for detail view, calm language)
  const warnings: string[] = []
  if (hasProducts) {
    if (!carbsInRange) {
      const ratio = targetChoG > 0 ? plannedChoG / targetChoG : 0
      if (ratio < CARB_TOLERANCE.low)
        warnings.push('Carbohydrates below target range')
      else if (ratio > CARB_TOLERANCE.high)
        warnings.push(
          'Carbohydrates above recommended — consider reducing slightly',
        )
    }
    if (!caloriesInRange) {
      const ratio = targetCalKcal > 0 ? plannedCalKcal / targetCalKcal : 0
      if (ratio < CAL_TOLERANCE.low)
        warnings.push('Calories below target range')
      else if (ratio > CAL_TOLERANCE.high)
        warnings.push('Calories above recommended — consider reducing slightly')
    }
    // Sodium warnings are informational only — they don't affect the segment state
    if (!sodiumInRange) {
      const ratio = targetSodiumMg > 0 ? plannedSodiumMg / targetSodiumMg : 0
      if (ratio < SODIUM_TOLERANCE.low)
        warnings.push('Sodium below target range')
      else if (ratio > SODIUM_TOLERANCE.high)
        warnings.push('Sodium above recommended — consider reducing slightly')
    }
  }

  return {
    segmentId: segment.id,
    targetChoG,
    targetCalKcal,
    targetFluidMl,
    targetSodiumMg,
    choRateGph: targetChoGph,
    calRateKcalph: targetCalKcalph,
    fluidRateMlph: targetFluidMlph,
    sodiumRateMgph: targetSodiumMgph,
    plannedChoG,
    plannedCalKcal,
    plannedSodiumMg,
    recommendedFluidMl: targetFluidMl,
    state,
    hasProducts,
    carbsInRange,
    caloriesInRange,
    sodiumInRange,
    carbsAboveTarget: carbsAboveTarget,
    caloriesAboveTarget: caloriesAboveTarget,
    sodiumAboveTarget: sodiumAboveTarget,
    productSummary,
    warnings,
  }
}

/**
 * Compute global nutrition state from all segment outputs.
 * - All on_target → "On target"
 * - Any needs_adjustment → "Needs adjustment"
 * - Any not_planned (and none needs_adjustment) → "Incomplete"
 */
export function computeGlobalNutritionState(
  outputs: SegmentNutritionOutput[],
): NutritionState {
  if (outputs.length === 0) return 'not_planned'

  let hasNeedsAdjustment = false
  let hasNotPlanned = false

  for (const output of outputs) {
    if (output.state === 'needs_adjustment') hasNeedsAdjustment = true
    if (output.state === 'not_planned') hasNotPlanned = true
  }

  if (hasNeedsAdjustment) return 'needs_adjustment'
  if (hasNotPlanned) return 'not_planned'
  return 'on_target'
}

/**
 * Human-readable label for nutrition state (calm, supportive language)
 */
export function getNutritionStateLabel(
  state: NutritionState,
  t?: (key: string) => string,
): string {
  if (t) {
    switch (state) {
      case 'not_planned':
        return t('nutrition.stateIncomplete')
      case 'needs_adjustment':
        return t('nutrition.stateNeedsAdjustment')
      case 'on_target':
        return t('nutrition.stateOnTarget')
    }
  }
  switch (state) {
    case 'not_planned':
      return 'Incomplete'
    case 'needs_adjustment':
      return 'Needs adjustment'
    case 'on_target':
      return 'On target'
  }
}
